import dayjs from "./dayjs"
import { filter, last, reduce, size, includes, keyBy, some } from "lodash"
import { SCHEDULER_ADMIN } from "@/constants"

export const clsx = (...classNames) =>
    Array.from(new Set(classNames.filter((cls) => cls))).join(" ")

export const cssUnit = (value, unit = "px") =>
    typeof value === "number" ? `${value}${unit}` : value

export const isFile = (file) => file instanceof File

export const isAsyncFunction = (func) => func?.constructor?.name === "AsyncFunction"

export const cleanObject = (obj) =>
    reduce(
        obj,
        (prev, curr, index) => {
            const nullish = [undefined, null]
            if (Array.isArray(curr)) {
                if (size(curr) === 0) return prev
                else if (some(curr, includes(nullish, curr))) return prev
            }
            if (includes(nullish, curr)) return prev
            return { ...prev, [index]: curr }
        },
        {}
    )

export const numberWithCommas = (x) =>
    x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0"

export const getHourFromTimestamp = (timestamp) => {
    if (isNaN(timestamp) || timestamp < 0) return 0
    const duration = dayjs.duration(timestamp, "milliseconds")
    const output = Math.floor(duration.asHours())
    return String(output).padStart(2, "0")
}

export const getMinuteFromTimestamp = (timestamp, isAddingHour = false) => {
    if (isNaN(timestamp) || timestamp < 0) return 0
    const duration = dayjs.duration(timestamp, "milliseconds")
    const output = isAddingHour
        ? duration.minutes() + 60 * getHourFromTimestamp(timestamp)
        : duration.minutes() // Get the minutes part
    return String(output).padStart(2, "0")
}

export const getSecondFromTimestamp = (timestamp, decimal = 0) => {
    if (isNaN(timestamp) || timestamp < 0 || isNaN(decimal) || decimal < 0) {
        return decimal ? `0.${"0".repeat(decimal)}` : 0
    }

    const duration = dayjs.duration(timestamp, "milliseconds")
    const seconds = String(duration.seconds()).padStart(2, "0")
    const milliseconds = duration.milliseconds()

    if (decimal) {
        const formattedMilliseconds = milliseconds.toString().padStart(3, "0").slice(0, decimal)
        return `${seconds}.${formattedMilliseconds}`
    }

    return seconds
}

export const getDisplayDuration = (milliseconds) =>
    [
        getHourFromTimestamp(milliseconds),
        getMinuteFromTimestamp(milliseconds),
        getSecondFromTimestamp(milliseconds)
    ].join(":")

export const secondToHHmmss = (seconds) => {
    let h = Math.floor(seconds / 3600)
    let m = Math.floor((seconds % 3600) / 60)
    let s = Math.round(seconds % 60)

    h = h < 10 ? "0" + h : h
    m = m < 10 ? "0" + m : m
    s = s < 10 ? "0" + s : s

    return h + ":" + m + ":" + s
}

export const getOpts = (data, key) => {
    const nullish = new Set(["", undefined, null])
    const arr = []
    for (const record of Array.from(data ?? [])) {
        const value = typeof key === "function" ? key(record) : record[key]
        if (Array.isArray(value)) {
            for (const v of value) {
                if (nullish.has(v)) continue
                arr.push(v)
            }
        } else {
            if (nullish.has(value)) continue
            arr.push(value)
        }
    }
    if (size(arr) > 0) {
        const list = [...new Set(arr)]
        list.sort()
        return list
    }
    return arr
}

export const getSelectOpts = (data, key) => {
    const arr = []
    const nullish = [null, undefined, ""]
    for (const value of Array.from(data?.[key] ?? [])) {
        if (nullish.includes(value)) continue
        arr.push(value)
    }
    if (size(arr) > 0) {
        const list = [...new Set(arr)]
        list.sort()
        return list.map((value) => ({ label: value, value }))
    }
    return arr
}

export const getLimitDateRange = (records, ...keys) => {
    const timestamps = []
    for (const record of records) {
        for (const key of keys) {
            const strDate = record[key] ?? ""
            let date = dayjs(strDate)
            if (date.isValid()) {
                // if (lowerCase(strDate).endsWith("z")) {
                //     date = date.utc()
                // }
                // if (lowerCase(strDate).includes("t")) {
                //     date = date.startOf("day")
                // }
                timestamps.push(date.unix())
            }
        }
    }

    if (size(timestamps) === 0) {
        return null
    }
    timestamps.sort()
    return [timestamps[0], last(timestamps)]
}

export const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes"

    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    const formattedValue = parseFloat((bytes / Math.pow(k, i)).toFixed(2))

    return `${formattedValue} ${sizes[i]}`
}

export const getMediaType = (contentType) => {
    return contentType?.split("/")?.[0]
}

export const sortAndFilterColumns = (widgets, orderConfigs, opts = {}) => {
    const { filterPredicate = {}, keyMapping = {} } = opts

    if (size(orderConfigs) === 0) return widgets

    const sortedWidgets = []
    const widgetByKeys = keyBy(widgets, "key")
    for (const order of filter(orderConfigs, filterPredicate)) {
        const widgetKeys = keyMapping[order.key] || [order.key]
        for (const key of widgetKeys) {
            if (key in widgetByKeys) {
                sortedWidgets.push({
                    ...widgetByKeys[key],
                    no: size(sortedWidgets) + 1
                })
            }
        }
    }
    return sortedWidgets
}

export const isAdminUser = (userInfo) =>
    userInfo?.is_superuser || includes(userInfo?.roles, SCHEDULER_ADMIN)

export const submit = (formId) => {
    const element = document.getElementById(formId)
    if (element instanceof Element) {
        const btn = document.createElement("button")
        btn.style.display = "none"
        btn.setAttribute("type", "submit")
        btn.setAttribute("form", formId)
        element.appendChild(btn)
        btn.click()
        element.removeChild(btn)
    }
}
